import React, { useState, useEffect } from 'react';
import { Briefcase, Plus, Search, X, AlertTriangle, Users } from 'lucide-react';
import Button from '../ui/Button';
import { apiFetch } from '../../services/apiClient';
import SkillDrawer from './SkillDrawer';
import PageHeader from '../ui/PageHeader';
import AddNewButton from '../ui/AddNewButton';
import ItemCard, { CARD_GRID, CardSection } from '../ui/ItemCard';
import { ControlBar, SearchBox } from '../ui/ControlBar';

const SkillsTab = ({ schedule, loadDemoData, getEmployeeColor }) => {
  const [skills, setSkills] = useState([]);
  const [loading, setSearchLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Modals & Drawers
  const [addModal, setAddModal] = useState(false);
  const [removeModal, setRemoveModal] = useState({ isOpen: false, name: '' });
  const [drawer, setDrawer] = useState({ isOpen: false, skill: null });
  
  // Form State
  const [skillName, setSkillName] = useState('');

  const fetchSkills = async () => {
    try {
      const response = await apiFetch('/api/skills');
      if (response.ok) {
        const data = await response.json();
        setSkills(data);
      }
    } catch (err) {
      console.error("Failed to fetch skills:", err);
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, []);

  const handleAdd = async () => {
    if (!skillName.trim()) return;
    setIsProcessing(true);
    try {
      const response = await apiFetch('/api/skills', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: skillName })
      });
      if (response.ok) {
        await fetchSkills();
        setAddModal(false);
        setSkillName('');
        if (loadDemoData) loadDemoData('SMALL');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRename = async (oldName, newName) => {
    if (!newName.trim()) return;
    const cleanOld = oldName.replace(/^skill_/i, '');
    if (newName === cleanOld) return;

    setIsProcessing(true);
    try {
      const response = await apiFetch(`/api/skills/${encodeURIComponent(oldName)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_name: newName })
      });
      if (response.ok) {
        await fetchSkills();
        if (loadDemoData) loadDemoData('SMALL');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemove = async () => {
    setIsProcessing(true);
    try {
      const response = await apiFetch(`/api/skills/${encodeURIComponent(removeModal.name)}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        await fetchSkills();
        setRemoveModal({ isOpen: false, name: '' });
        if (loadDemoData) loadDemoData('SMALL');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAssign = async (skill, staffIds) => {
    try {
      const response = await apiFetch(`/api/skills/${encodeURIComponent(skill)}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ staff_ids: staffIds })
      });
      if (response.ok) {
        if (loadDemoData) await loadDemoData('SMALL');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const filteredSkills = skills.filter(s => 
    s.replace(/^skill_/i, '').toLowerCase().includes(searchQuery.toLowerCase())
  ).sort();

  return (
    <div className="animate-in fade-in space-y-6">
      {/* ADD MODAL */}
      {addModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#042f2e]/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-sm border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">New Skill</h3>
                <button onClick={() => setAddModal(false)} className="p-2 hover:bg-slate-50 rounded-full text-slate-400 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-6 mb-8">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Skill Name</label>
                  <input 
                    type="text" 
                    value={skillName} 
                    onChange={(e) => setSkillName(e.target.value)} 
                    placeholder="e.g. ICU Qualified" 
                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[var(--primary)]/20 outline-none transition-all text-sm font-bold text-slate-700 shadow-sm" 
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setAddModal(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleAdd} disabled={isProcessing || !skillName.trim()} isLoading={isProcessing} className="flex-1" icon={Plus}>Create</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REMOVE MODAL */}
      {removeModal.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#042f2e]/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-sm border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-6"><AlertTriangle className="w-8 h-8" /></div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Delete Skill</h3>
              <p className="text-sm text-slate-500 font-medium mb-8">Are you sure you want to remove <span className="font-bold text-slate-900">{removeModal.name.replace(/^skill_/i, '')}</span>? This will remove it from all staff and locations.</p>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setRemoveModal({ isOpen: false, name: '' })} className="flex-1">Cancel</Button>
                <Button variant="primary" onClick={handleRemove} isLoading={isProcessing} className="flex-1 bg-rose-500 hover:bg-rose-600 border-none shadow-rose-200">Delete</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SKILL ASSIGNMENT DRAWER */}
      <SkillDrawer 
        isOpen={drawer.isOpen}
        onClose={() => setDrawer({ isOpen: false, skill: null })}
        skill={drawer.skill}
        employees={schedule?.employees || []}
        onAssign={handleAssign}
        onRename={handleRename}
        getEmployeeColor={getEmployeeColor}
      />

      {/* FLOATING IDENTITY */}
      <PageHeader icon={Briefcase} title="Skills Library" />

      <ControlBar>
        <SearchBox value={searchQuery} onChange={setSearchQuery} placeholder="Search skills..." />
      </ControlBar>

      {/* One card per skill (Oct 2026: every list is cards). */}
      <div className={CARD_GRID}>
        {filteredSkills.map(skill => {
          const holders = (schedule?.employees || []).filter(e => (e.skills || []).includes(skill));
          return (
            <ItemCard key={skill} title={skill.replace(/^skill_/i, '')}
              onEdit={() => setDrawer({ isOpen: true, skill })}
              onDelete={() => setRemoveModal({ isOpen: true, name: skill })}
              footerLeft={`${holders.length} qualified`}>
              <CardSection icon={Users} label="Held by">
                <div className="flex flex-wrap gap-1.5">
                  {holders.slice(0, 8).map(e => (
                    <span key={e.id || e.name} className="px-2 py-1 bg-slate-50 border border-slate-100 rounded-md text-[9px] font-bold text-slate-600 shadow-sm">{e.name}</span>
                  ))}
                  {holders.length > 8 && <span className="text-[9px] font-bold text-slate-400 self-center">+{holders.length - 8} more</span>}
                  {holders.length === 0 && <span className="text-[9px] text-slate-300 italic">Nobody yet</span>}
                </div>
              </CardSection>
            </ItemCard>
          );
        })}

        {filteredSkills.length === 0 && !loading && (
          <div className="col-span-full py-24 text-center">
            <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
              <Search className="w-6 h-6 text-slate-300" />
            </div>
            <h3 className="text-base font-bold text-slate-900">No skills found</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-[240px] mx-auto leading-relaxed">
              Refine your search, or add a new skill below.
            </p>
          </div>
        )}
      </div>

      {/* Adding, at the bottom of the list like everywhere else (Oct 2026). */}
      <AddNewButton label="New skill" onClick={() => { setSkillName(''); setAddModal(true); }} className="mt-8" />
    </div>
  );
};

export default SkillsTab;
